/**
 * Component: Multi Thumb Slider
 *
 * @license
 * This software or document includes material copied from or derived from multithumb-slider.js (https://w3c.github.io/aria-practices/examples/slider/js/multithumb-slider.js). Copyright © 2018 W3C® (MIT, ERCIM, Keio, Beihang).
 * The original code is licensed according to the W3C Software License at https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 */

// Import extend function
import extend from '../../../js/utilities/extend/u-extend.js';
import ajax from '../../../js/utilities/ajax/u-ajax.js';

class ThumbSlider {

	/**
	 * Constructor, merge settings and creates markup
	 * @param  {Object} settings User settings
	 */
	constructor (element, settings = {}) {

		// Default settings
		this.defaults = {
			inputFieldContainerSelector: '.c-thumb-slider__input-fields',
			inputField: '.c-thumb-slider__input-field',
			inputLabel: '.c-thumb-slider__input-field label',
			inputSelector: '.c-thumb-slider__input-field input',
			sliderContainerClass: 'c-thumb-slider__slider',
			labelContainerClass: 'c-thumb-slider__rail-label',
			labelContainerLabelPrefix: 'Possible range: ',
			railClass: 'c-thumb-slider__rail',
			railRangeClass: 'c-thumb-slider__rail-range',
			thumbClass: 'c-thumb-slider__thumb',
			valueTextPrefix: '',
			valueTextSuffix: '',
			thumbWidth: 24,
			intervalData: '',
		};

		this.container = element;

		// Merge user settings with defaults
		this.settings = extend(this.defaults, settings);

		// Create markup
		this.init();
	}

	/**
	 * Create DOM elements and set up event listeners
	 */
	init () {

		// Store references to the input elements
		this.input = this.container.querySelector(this.settings.inputSelector);

		// Create output mask
		this.outputField = document.createElement('output');
		this.outputField.classList.add('c-thumb-slider__output');
		this.outputValue = document.createElement('span');
		this.outputValue.classList.add('c-thumb-slider__output-value');
		this.outputUnit = document.createElement('span');
		this.outputUnit.classList.add('c-thumb-slider__output-unit');

		// Populate Output mask
		// this.outputValue.textContent = this.input.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
		// Value above is generated when collecting intervalData
		this.inputLabel = this.container.querySelector(this.settings.inputLabel);
		this.outputUnit.textContent = ' ' + this.inputLabel.dataset.unit;
		this.inputField = this.container.querySelector(this.settings.inputField);
		this.inputField.appendChild(this.outputField);
		this.generatedOutput = this.inputField.querySelector('.c-thumb-slider__output');
		this.generatedOutput.appendChild(this.outputValue);
		this.generatedOutput.appendChild(this.outputUnit);
		this.input.classList.add('is-masked');

		// Interpret value for hidden input field
		this.interpretValue = this.container.querySelector('.c-thumb-slider__interpret-value');

		// The slider container
		this.sliderContainer = document.createElement('div');
		this.sliderContainer.setAttribute('class', this.settings.sliderContainerClass);

		// The label container
		this.labelContainer = document.createElement('div');
		this.labelContainer.setAttribute('class', this.settings.labelContainerClass);
		// this.labelContainer.setAttribute('aria-label', this.settings.labelContainerLabelPrefix + this.input.min + ' - ' + this.input.max);
		// Adding an aria-label ends up in validation issues, adding a visually hidden element instead resolves the issues
		this.labelContainerContent = document.createElement('span');
		this.labelContainerContentText = document.createTextNode(this.settings.labelContainerLabelPrefix + this.input.min + ' - ' + this.input.max);
		this.labelContainerContent.setAttribute('class', 't-visually-hidden');
		this.labelContainerContent.appendChild(this.labelContainerContentText);
		this.labelContainer.appendChild(this.labelContainerContent);

		this.sliderContainer.appendChild(this.labelContainer);

		// The rail
		this.rail = document.createElement('div');
		this.rail.setAttribute('class', this.settings.railClass);
		this.sliderContainer.appendChild(this.rail);

		this.thumb = document.createElement('span');
		this.thumb.input = this.input;
		this.thumb.setAttribute('id', this.input.id + '-thumb');
		this.thumb.setAttribute('role', 'slider');
		this.thumb.setAttribute('tabindex', '0');
		this.thumb.setAttribute('class', this.settings.thumbClass);
		this.thumb.setAttribute('aria-valuemin', this.input.min);
		this.thumb.setAttribute('aria-valuemax', this.input.max);
		this.thumb.setAttribute('aria-valuenow', this.input);
		this.thumb.setAttribute('aria-valuetext', this.settings.valueTextPrefix + this.input.value + this.settings.valueTextSuffix);
		this.thumb.setAttribute('aria-label', this.container.querySelector('label[for="' + this.input.id + '"]').textContent);
		this.rail.appendChild(this.thumb);

		this.railRange = document.createElement('span');
		this.railRange.setAttribute('class', this.settings.railRangeClass);
		this.railRange.setAttribute('aria-hidden', 'true');
		this.rail.appendChild(this.railRange);

		// Store the original min and max values of the rail and the input fields
		this.railMin = parseInt(this.input.min, 10);
		this.railMax = parseInt(this.input.max, 10);

		// Add event listeners to the thumb container
		this.sliderContainer.addEventListener('keydown', this.handleKeyDown.bind(this));
		this.sliderContainer.addEventListener('touchstart', this.handleMouseDown.bind(this));
		this.sliderContainer.addEventListener('mousedown', this.handleMouseDown.bind(this));
		this.sliderContainer.addEventListener('focus', this.handleFocus.bind(this));
		this.sliderContainer.addEventListener('blur', this.handleBlur.bind(this));

		// Insert the created elements
		// this.container.insertBefore(this.sliderContainer, this.container.querySelector(this.settings.inputFieldContainerSelector));
		this.container.appendChild(this.sliderContainer);

		// Set the thumbs to their initial positions
		this.positionThumbs();

		// Reposition the thumbs if the user changes the value of the input fields directly
		this.input.addEventListener('change', this.positionThumbs.bind(this));
		this.input.addEventListener('change', this.changeOutput.bind(this));

		// Reposition the thumbs when the viewport (and thus the rail) is resized
		window.addEventListener('resize', this.positionThumbs.bind(this));
		window.addEventListener('orientationchange', this.positionThumbs.bind(this));

		// Gets JSON intervalData
		if(this.container) {
			window.addEventListener('load', this.getIntervalData.bind(this));	
		}

		// // Append button icon
		// this.thumb.appendChild(icon({
		// 	icon: this.settings.icon,
		// 	size: 'm',
		// 	wrapperClass: 'c-icon',
		// 	extra_class: [
		// 		this.settings.iconClass
		// 	]
		// }));
	}

	getIntervalData () {
		let interpretValueUrl = this.interpretValue.getAttribute('data-url');
		ajax({
			url: interpretValueUrl,
			dataType: 'json',
			onSuccess: (response) => {
				if(response.incomes) {
					this.settings.intervalData = response.incomes;
				}

				// Init changeOutput when ajax call is done to get data and display in the hidden input field and the visible output field
				this.changeOutput();
			}
		});
	}

	changeOutput () {
		if(this.settings.intervalData.length > 0) {

			// Loop through the intervalData where every object has min,max,value.
			for (var i = 0; i < this.settings.intervalData.length; i++) {
				var interval = this.settings.intervalData[i];

				// Check which thumb slider value that is matching which interval
				if(this.input.value >= interval.min && this.input.value <= interval.max) {

					this.interpretValue.value = interval.value;

					if(interval.value === 0) {
						this.outputValue.textContent = 0;
					}
					else {
						// Check if the data is the last one in the array, if so the output text is different
						if(this.settings.intervalData.length - 1 == i) {
							this.outputValue.textContent = interval.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + '+';
						}
						else {
							this.outputValue.textContent = interval.min.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' - ' + interval.max.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
						}
					}
				}
			}
		}
	}

	positionThumbs () {
		this.moveThumbTo(this.thumb, this.input.value, this.railRange);
	}

	moveThumbTo (thumb, value) {

		const railRange = thumb.parentNode.querySelector('.c-thumb-slider__rail-range');
		const valueMax = parseInt(thumb.input.max);
		const valueMin = parseInt(thumb.input.min);

		if (value >= valueMax) {
			value = valueMax;
		}

		if (value <= valueMin) {
			value = valueMin;
		}

		let valueStep;
		if (this.input.getAttribute('step') != null) {
			valueStep = parseInt(this.input.getAttribute('step'));
		}
		else {
			valueStep = 1;
		}
		value = valueStep * Math.round(value / valueStep);

		const pos = Math.round(((value - this.railMin) * (this.rail.clientWidth - 0.55 * this.settings.thumbWidth)) / (this.railMax - this.railMin));
		thumb.setAttribute('aria-valuenow', value);
		thumb.setAttribute('aria-valuetext', this.settings.valueTextPrefix + value + this.settings.valueTextSuffix);
		thumb.input.value = value;
		this.changeOutput();

		thumb.style.left = pos + 'px';
		railRange.style.right = 'calc(100% - ' + (pos + this.settings.thumbWidth) + 'px)';

	}

	handleKeyDown (e) {

		const keyCodes = {
			'left': 37,
			'up': 38,
			'right': 39,
			'down': 40,
			'pageUp': 33,
			'pageDown': 34,
			'end': 35,
			'home': 36
		};

		// Consume the keyDown event if one of the keys we use was pressed
		if (Object.keys(keyCodes).some(function(key) {
			return keyCodes[key] === e.keyCode;
		})) {
			e.preventDefault();
			e.stopPropagation();
		}

		const thumb = e.target;
		const valueNow = parseInt(thumb.getAttribute('aria-valuenow'), 10);

		let valueStep;
		if (this.input.getAttribute('step') != null) {
			valueStep = parseInt(this.input.getAttribute('step'));
		}
		else {
			valueStep = 1;
		}

		switch (e.keyCode) {
			case keyCodes.left:
			case keyCodes.down:
				this.moveThumbTo(thumb, valueNow - valueStep);
				break;

			case keyCodes.right:
			case keyCodes.up:
				this.moveThumbTo(thumb, valueNow + valueStep);
				break;

			case keyCodes.pageDown:
				this.moveThumbTo(thumb, valueNow - valueStep);
				break;

			case keyCodes.pageUp:
				this.moveThumbTo(thumb, valueNow + valueStep);
				break;

			case keyCodes.home:
				this.moveThumbTo(thumb, this.railMin);
				break;

			case keyCodes.end:
				this.moveThumbTo(thumb, this.railMax);
				break;
		}
	}

	handleFocus () {
		this.rail.classList.add('focus');
	}

	handleBlur () {
		this.rail.classList.remove('focus');
	}

	handleMouseDown (event) {
		// Return if the click was not on a handle
		if (!event.target.hasAttribute('role') || event.target.getAttribute('role') !== 'slider') {
			return;
		}
		const thumb = event.target;
		const slider = this;

		function handleMouseMove (event) {
			event.preventDefault();
			event.stopPropagation();
			var clientX = null;
			if (event.clientX != null) {
				clientX = event.clientX;
			}
			else if (event.touches[0].clientX != null) {
				clientX = event.touches[0].clientX;
			}
			const diffX = clientX - Math.round(slider.rail.getBoundingClientRect().left);
			slider.moveThumbTo(thumb, slider.railMin + parseInt(((slider.railMax - slider.railMin) * diffX) / slider.rail.clientWidth, 10));
		}

		function handleMouseUp () {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('touchmove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.removeEventListener('touchend', handleMouseUp);
		}

		// Track mouse movements and move the thumb
		document.addEventListener('touchmove', handleMouseMove);
		document.addEventListener('mousemove', handleMouseMove);

		// Stop tracking mouse movements on mouseup
		document.addEventListener('touchend', handleMouseUp);
		document.addEventListener('mouseup', handleMouseUp);

		event.preventDefault();
		event.stopPropagation();

		// Move focus to the clicked handle
		thumb.focus();
	}
}

export default ThumbSlider;
